<template>
  <div class="form-container">
    <div class="form-header">
      <h2>Edit Video File</h2>
    </div>
    <form @submit.prevent="handleSubmit" class="form">
      <div class="form-section">
        <h3>File Information</h3>
        <div class="form-group half-width">
          <label for="file_name">File Name</label>
          <input
            id="file_name"
            v-model="formData.file_name"
            type="text"
            class="form-control"
            maxlength="80"
          >
        </div>

        <div class="form-group half-width">
          <label for="channel" class="required">Channel</label>
          <select 
            id="channel"
            v-model="formData.channel_id"
            class="form-control"
            required
          >
            <option value="">Select a channel</option>
            <option v-for="channel in sortedChannels" :key="channel.id" :value="channel.id">
              {{ channel.name }}
            </option>
          </select>
        </div>

        <div class="form-group half-width">
          <label for="file_nickname">File Nickname</label>
          <input
            id="file_nickname"
            v-model="formData.file_nickname"
            type="text"
            class="form-control"
            maxlength="255"
          >
        </div>

        <div class="form-group half-width">
          <label for="format">Format</label>
          <select
            id="format"
            v-model="formData.format"
            class="form-control"
          >
            <option value="">Select format</option>
            <option v-for="format in formatOptions" :key="format.value" :value="format.value">
              {{ format.display }}
            </option>
          </select>
        </div>

        <div class="form-group full-width">
          <label for="file_url">File URL</label>
          <input
            id="file_url"
            v-model="formData.file_url"
            type="text"
            class="form-control"
            maxlength="255"
          >
        </div>
      </div>

      <div class="form-section">
        <h3>Technical Details</h3>
        <div class="form-group quarter-width">
          <label for="duration">Duration (seconds)</label>
          <input
            id="duration"
            v-model.number="formData.duration"
            type="number"
            class="form-control"
            min="0"
          >
        </div>

        <div class="form-group quarter-width">
          <label for="file_size">File Size (bytes)</label>
          <input
            id="file_size"
            v-model.number="formData.file_size"
            type="number"
            class="form-control"
            min="0"
          >
        </div>

        <div class="form-group quarter-width">
          <label for="file_resolution">Resolution</label>
          <input
            id="file_resolution"
            v-model="formData.file_resolution"
            type="text"
            class="form-control"
            maxlength="20"
            placeholder="e.g. 1920x1080"
          >
        </div>

        <div class="form-group quarter-width">
          <label for="file_framerate">Framerate</label>
          <input
            id="file_framerate"
            v-model.number="formData.file_framerate"
            type="number"
            class="form-control"
            min="0"
          >
        </div>

        <div class="form-group quarter-width">
          <label for="file_format">File Format</label>
          <input
            id="file_format"
            v-model="formData.file_format"
            type="text"
            class="form-control"
            maxlength="20"
          >
        </div>

        <div class="form-group quarter-width">
          <label for="file_codec_id">Codec ID</label>
          <input
            id="file_codec_id"
            v-model="formData.file_codec_id"
            type="text"
            class="form-control"
            maxlength="20"
          >
        </div>

        <div class="form-group quarter-width">
          <label for="file_bit_depth">Bit Depth</label>
          <input
            id="file_bit_depth"
            v-model.number="formData.file_bit_depth"
            type="number"
            class="form-control"
            min="0"
          >
        </div>

        <div class="form-group quarter-width">
          <label for="file_bit_rate">Bit Rate</label>
          <input
            id="file_bit_rate"
            v-model.number="formData.file_bit_rate"
            type="number"
            class="form-control"
            min="0"
          >
        </div>

        <div class="form-group half-width">
          <label for="file_encoding_date">Encoding Date</label>
          <input
            id="file_encoding_date"
            v-model="formData.file_encoding_date"
            type="datetime-local"
            class="form-control"
          >
        </div>
      </div>

      <div class="form-section">
        <h3>Stream Information</h3>
        <div class="form-group half-width">
          <label for="stream_status">Stream Status</label>
          <select
            id="stream_status"
            v-model="formData.stream_status"
            class="form-control"
          >
            <option value="">Select status</option>
            <option v-for="status in streamStatusOptions" :key="status.value" :value="status.value">
              {{ status.display }}
            </option>
          </select>
        </div>

        <div class="form-group half-width">
          <label for="stream_status_updated_at">Status Updated At</label>
          <input
            id="stream_status_updated_at"
            v-model="formData.stream_status_updated_at"
            type="datetime-local"
            class="form-control"
          >
        </div>

        <div class="form-group half-width">
          <label for="stream_bitrate_ladder">Stream Bitrate Ladder</label>
          <input
            id="stream_bitrate_ladder"
            v-model="formData.stream_bitrate_ladder"
            type="text"
            class="form-control"
            maxlength="255"
          >
        </div>

        <div class="form-group half-width">
          <label for="stream_url">Stream URL</label>
          <input
            id="stream_url"
            v-model="formData.stream_url"
            type="text"
            class="form-control"
            maxlength="255"
          >
        </div>

        <div class="form-group half-width">
          <label for="spatialgen_video_id">SpatialGen Video ID</label>
          <input
            id="spatialgen_video_id"
            v-model="formData.sg_video_id"
            type="text"
            class="form-control"
            maxlength="36"
          >
        </div>

        <div class="form-group half-width">
          <label for="spatialgen_job_id">SpatialGen Job ID</label>
          <input
            id="spatialgen_job_id"
            v-model="formData.sg_job_id"
            type="text"
            class="form-control"
            maxlength="255"
          >
        </div>

        <div class="form-group half-width">
          <label for="spatialgen_job_progress">SpatialGen Job Progress</label>
          <input
            id="spatialgen_job_progress"
            v-model.number="formData.sg_job_progress"
            type="number"
            class="form-control"
            min="0"
            max="100"
          >
        </div>
      </div>

      <div class="form-actions">
        <button type="submit" class="submit-button big">Save Changes</button>
        <button type="button" class="cancel-button medium" @click="handleCancel">Cancel</button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { handleApiError } from '@/scripts/shared-utils';
import { formatOptions, streamStatusOptions } from '@/scripts/shared-video';

const route = useRoute();
const router = useRouter();
const videoFileId = ref(route.params.video_file_id);
const channels = ref([]);

const formData = ref({
  channel_id: '',
  format: '',
  duration: null,
  file_name: '',
  file_nickname: '',
  file_url: '',
  file_size: null,
  file_resolution: '',
  file_framerate: null,
  file_format: '',
  file_codec_id: '',
  file_bit_depth: null,
  file_bit_rate: null,
  file_encoding_date: '',
  stream_status: '',
  stream_status_updated_at: '',
  stream_bitrate_ladder: '',
  stream_url: '',
  sg_video_id: '',
  sg_job_id: '',
  sg_job_progress: null,
});

const sortedChannels = computed(() => {
  return [...channels.value].sort((a, b) => {
    return a.name.localeCompare(b.name);
  });
});

const fetchVideoFile = async () => {
  try {
    const response = await axios.get(`/api/video-files/${videoFileId.value}`);
    if (response.status === 200) {
      const data = response.data;
      // Convert dates to local datetime-local format
      if (data.file_encoding_date) {
        data.file_encoding_date = new Date(data.file_encoding_date).toISOString().slice(0, 16);
      }
      if (data.stream_status_updated_at) {
        data.stream_status_updated_at = new Date(data.stream_status_updated_at).toISOString().slice(0, 16);
      }
      formData.value = data;
    }
  } catch (error) {
    handleApiError(error, 'fetching video file');
    alert('Error loading video file. Please try again.');
  }
};

const fetchChannels = async () => {
  try {
    const response = await axios.get('/api/channels');
    channels.value = response.data;
  } catch (error) {
    handleApiError(error, 'fetching channels');
  }
};

const handleSubmit = async () => {
  try {
    const editableFields = [
      'channel_id',
      'format',
      'duration',
      'file_name',
      'file_nickname',
      'file_url',
      'file_size',
      'file_resolution',
      'file_framerate',
      'file_format',
      'file_codec_id',
      'file_bit_depth',
      'file_bit_rate',
      'file_encoding_date',
      'stream_status',
      'stream_status_updated_at',
      'stream_bitrate_ladder',
      'stream_url',
      'sg_video_id',
      'sg_job_id',
      'sg_job_progress'
    ];
    
    const payload = {};
    editableFields.forEach(field => {
      if (field in formData.value) {
        payload[field] = formData.value[field];
      }
    });
    
    // Convert datetime-local back to ISO format
    if (payload.file_encoding_date) {
      payload.file_encoding_date = new Date(payload.file_encoding_date).toISOString();
    }
    if (payload.stream_status_updated_at) {
      payload.stream_status_updated_at = new Date(payload.stream_status_updated_at).toISOString();
    }
    if (payload.sg_job_progress === "") {
      payload.sg_job_progress = null;
    }
    
    await axios.put(`/api/video-files/${videoFileId.value}`, payload);
    closeForm();
  } catch (error) {
    handleApiError(error, 'updating video file');
    alert('Error saving video file. Please try again.');
  }
};

const handleCancel = () => {
  router.back();
};

const closeForm = () => {
  router.back();
};

onMounted(async () => {
  await fetchVideoFile();
  await fetchChannels();
});
</script>

<style>
@import '@/styles/shared-form.css';
</style>
